import axios from "axios";
import lodash from 'lodash';
import {
  sysoApiConfig,
  siteToModeMap,
  scalingMap,
  allowedIESOSystemIds,
  allowedSolarOnlySystemIds,
  breakerStatusMap,
} from '../apiConfig';

export const getGeneralAPIRequest = (msSessionInfo) => {
  const apiConfig = {
    baseURL: sysoApiConfig.baseSYSOUrl,
    headers: { Authorization: `Bearer ${msSessionInfo.accessToken}` }
  };
  return axios.create(apiConfig);
}

export const formatPowerWithUnit = (power, systemId) => {
  const isOneida = allowedIESOSystemIds.has(systemId);
  const unit = isOneida ? 'MW' : 'kW';
  if (typeof power !== 'number') return `undefined ${unit}`;
  return `${power.toFixed(2)} ${unit}`;
};

export function getUnitForPower(systemId) {
  return allowedIESOSystemIds.has(systemId) || allowedSolarOnlySystemIds.has(systemId) ? 'MW' : 'kW';
}

export function getUnitForEnergy(systemId) {
  return allowedIESOSystemIds.has(systemId) ? '(MWH)' : '(kWH)';
}

export function getUnitForLMPPrice(systemId) {
  return allowedIESOSystemIds.has(systemId) ? '($/MWH)' : '($/kWH)';
}

// =========================
// BESS Status Data Transformations
export const getCardColor = (modeText, power) => {
  const threshold = 20;
  // if (modeText === 'Shutdown') return 'disable';
  if (power === undefined) return 'warning';
  if (power < threshold && power > -threshold) return 'disable';
  // if (!modeText || modeText === 'Unknown') return 'error';
  return 'secondary';
};


export const getModeText = (mode, stsId) => {
  const modeMap = siteToModeMap.get(stsId);
  // If no specific mode map for this site, return the mode as string
  if (!modeMap) return mode?.toString() || 'Unknown';
  return modeMap.get(mode) || mode?.toString();
};


export const getChargingStatus = (power, modeText) => {
  const threshold = 20;
  // if (color !== 'secondary') return modeText;
  if (power === undefined || power === null) return "Offline";
  if (power > threshold) return 'Discharging';
  if (power < -threshold) return 'Charging';
  return 'Hold';
};

export const getSocPercent = (soc) => {
  if (soc === undefined || soc === null) return '-';
  return `${(soc / 10).toFixed(1)} %`;
};

export const getSolarOutputText = (kw, systemId) => {
  const unit = getUnitForPower(systemId);
  if (typeof kw !== 'number') return `- ${unit}`;
  return `${lodash.round(scaleData(kw, 'divide1000'), 2)} ${unit}`;
};

export const getBreakerStatusText = (status) => {
  if (status === undefined || status === null) return '-';
  return breakerStatusMap.get(status) || status.toString();
};

export const generateSolarOnlyTableData = (breakerStatus) => [
  { label: 'Breaker Status', value: getBreakerStatusText(breakerStatus) },
];

export const generateSiteTableData = (bessData) => {
  if (!bessData) return [];
  const isOneida = allowedIESOSystemIds.has(bessData.systemId);
  const modeText = getModeText(bessData.mode, bessData.stsId);
  const cardColor = getCardColor(modeText, bessData.power);
  const chargingStatus = getChargingStatus(bessData.power, cardColor, modeText);
  const powerDisplay = isOneida
    ? formatPowerWithUnit(bessData.power, bessData.systemId)
    : `${chargingStatus} (${formatPowerWithUnit(bessData.power, bessData.systemId)})`;
  return [
    { label: 'Current Mode', value: modeText },
    { label: 'Current BESS Power', value: powerDisplay },
    { label: 'Current SOC', value: getSocPercent(bessData.soc) },
  ];
};

export const transformChartData = (chartData) => {
  const scalingDefault = scalingMap.get('default');
  const dataPreppedForCharts = {};

  if (chartData.tsStatus && chartData.tsStatus.power && chartData.tsStatus.soc) {
    dataPreppedForCharts.power = readChart(chartData.tsStatus.power, scalingDefault.power, 2);
    dataPreppedForCharts.soc = readChart(chartData.tsStatus.soc, scalingDefault.soc, 2);
  }
  if (chartData.tsStatus && chartData.tsStatus.kw) {
    dataPreppedForCharts.solar = readChart(chartData.tsStatus.kw, 'divide1000', 2);
  }

  if (chartData.tsStatus && chartData.tsStatus.poi_output && chartData.tsStatus.poi_output.length >= 1) {
    const poi = readChart(chartData.tsStatus.poi_output, null, 2);
    const combinedTable = combineTwoTimeseries(poi, dataPreppedForCharts.power)
    dataPreppedForCharts.poi = combinedTable.dataSetOne;
    const pvToGrid = []
    const bessImport = []
    const bessExport = []
    combinedTable.dataSetTwo.dataSet.forEach((num, index) => {
      const gridOutput = combinedTable.dataSetOne.dataSet[index];
      if (num < 0) {
        bessImport.push(-1 * num);
        bessExport.push(0);
        pvToGrid.push(gridOutput);
      } else {
        bessExport.push(num);
        bessImport.push(0);
        const pvToGridVar = lodash.round(gridOutput - num, 2);
        if (pvToGridVar < 0) {
          pvToGrid.push(0);
        } else {
          pvToGrid.push(pvToGridVar);
        }
      }
    });
    dataPreppedForCharts.bessImport = { "labels": combinedTable.dataSetTwo.labels, "dataSet": bessImport }
    dataPreppedForCharts.bessExport = { "labels": combinedTable.dataSetTwo.labels, "dataSet": bessExport }
    dataPreppedForCharts.pvToGrid = { "labels": combinedTable.dataSetTwo.labels, "dataSet": pvToGrid }
  }

  if (chartData.pricing && chartData.pricing.prices) {
    dataPreppedForCharts.prices = readChart(chartData.pricing.prices, 'usePrice');
  }

  if (chartData.optimization) {
    dataPreppedForCharts.optimSOC = readChart(chartData.optimization.soc, 'multiply100', 1);
    dataPreppedForCharts.vderOptimPower = readChart(chartData.optimization.vder.power, null, 2);
    dataPreppedForCharts.dlmOptimPower = readChart(chartData.optimization.dlm.power, null, 2);
  }

  return dataPreppedForCharts;
}


const scaleData = (oldValue, transform) => {
  if (transform === 'divide10') {
    return (oldValue / 10);
  }
  if (transform === 'divide100') {
    return (oldValue / 100);
  }
  if (transform === 'divide1000') {
    return (oldValue / 1000);
  }
  if (transform === 'multiply100') {
    return (oldValue * 100);
  }
  return (oldValue);
}


const readChart = (selectedChart, transform, decimalPlaces) => {
  if (decimalPlaces === null) {
    decimalPlaces = 0;
  }
  // Converts [{time:x, value:y}] to separate list of labels (x-axis) and data (y-axis)
  const labels = [];
  const dataSet = [];
  for (let i = 0; i < selectedChart.length; i += 1) {
    labels.push(selectedChart[i].time);
    if (transform === 'usePrice') {
      let newPriceValue = scaleData(selectedChart[i].price, 'roundTwo');
      newPriceValue = newPriceValue.toFixed(2);
      dataSet.push(newPriceValue);
    } else {
      let newValue = scaleData(selectedChart[i].value, transform);
      newValue = lodash.round(newValue, decimalPlaces);
      dataSet.push(newValue);
    }
  }
  return { "labels": labels, "dataSet": dataSet };
}


export const combineTwoTimeseries = (tsOne, tsTwo) => {
  const combinedTimeStamps = lodash.sortBy(lodash.union(tsOne.labels, tsTwo.labels));
  const tsOneTimeToValMap = new Map();
  const tsTwoTimeToValMap = new Map();

  tsOne.labels.forEach((label, index) => {
    tsOneTimeToValMap.set(label, tsOne.dataSet[index]);
  });

  tsTwo.labels.forEach((label, index) => {
    tsTwoTimeToValMap.set(label, tsTwo.dataSet[index]);
  });

  const combinedData = {}
  combinedData.dataSetOne = { "labels": combinedTimeStamps, "dataSet": [] }
  combinedData.dataSetTwo = { "labels": combinedTimeStamps, "dataSet": [] }

  combinedTimeStamps.forEach((timeStamp) => {
    let tsOneVal = tsOneTimeToValMap.get(timeStamp);
    let tsTwoVal = tsTwoTimeToValMap.get(timeStamp);
    tsOneVal = tsOneVal === undefined ? 0 : tsOneVal
    tsTwoVal = tsTwoVal === undefined ? 0 : tsTwoVal
    combinedData.dataSetOne.dataSet.push(tsOneVal);
    combinedData.dataSetTwo.dataSet.push(tsTwoVal);
  });
  return combinedData;
}


export const fetchSystemsData = async (accessToken) => {
  try {
    const api = getGeneralAPIRequest(accessToken);
    const response = await api.get('/systems');
    return response.data;
  } catch (error) {
    console.error("API fetch error:", error?.response);
    throw error;
  }
};

export const fetchSitesData = async (accessToken) => {
  try {
    const api = getGeneralAPIRequest(accessToken);
    const response = await api.get('/sites');
    return response.data;
  } catch (error) {
    console.error("API fetch error:", error?.response);
    throw error;
  }
};


export const getBessStatusBySystemId = async (axiosSession, systemIds, points = []) => {
  try {
    const paramsVar = new URLSearchParams();
    systemIds.forEach(id => paramsVar.append("systemIds", id));
    points.forEach(p => paramsVar.append("points", p));
    const request = { params: paramsVar };
    const response = await axiosSession.get('/bess/status', request);
    if (response.status === 200) {
      return { data: response.data, error: null };
    }
    return { data: null, error: 'Offline' };
  } catch (error) {
    return { data: null, error: 'Offline' };
  }
};


export const fileDownload = (fileName, fileData) => {
  const href = URL.createObjectURL(fileData);
  const link = document.createElement('a');
  link.href = href;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(href);
}